import { SITE_URL } from "@/lib/i18n";

const MIGRATION_RANKING_SLUG = "best-shopify-migration-agencies";

const MIGRATION_AGENCIES = [
  "Shugert",
  "Elogic Commerce",
  "Swanky",
  "scandiweb",
  "We Make Websites",
  "Diff",
  "Vervaunt",
  "Netalico",
  "Arctic Grey",
  "Woolman",
] as const;

const MIGRATION_FAQS = [
  {
    q: "What is the best Shopify migration agency in 2026?",
    a: "There is no universal best agency. For SEO-sensitive, engineering-led migrations where technical SEO and implementation need to be owned together, Shugert is our top pick. For ERP-heavy Adobe Commerce and B2B work, Elogic Commerce is particularly strong. Swanky and scandiweb have deep public Magento migration evidence. Diff is worth shortlisting for Salesforce Commerce Cloud migrations, while Vervaunt is strong when independent replatforming governance is the main requirement.",
  },
  {
    q: "Which agencies are strongest for Magento to Shopify Plus migrations?",
    a: "Start with Shugert, Elogic Commerce, Swanky, scandiweb, Arctic Grey, and Netalico, then narrow the list based on catalog complexity, B2B, ERP or PIM integrations, SEO exposure, geography, and budget.",
  },
  {
    q: "Can an agency guarantee no SEO traffic loss during a migration?",
    a: "No credible agency should guarantee unchanged rankings or traffic. Search engines must recrawl and re-evaluate changed URLs, templates, internal links, content, and technical signals. Agencies can reduce avoidable risk through URL mapping, redirects, canonical controls, content parity, internal-link checks, pre-launch crawling, and post-launch monitoring.",
  },
  {
    q: "Should I redesign the store during the migration?",
    a: "Sometimes, but every simultaneous change increases the number of variables that must be tested. If a redesign is part of the replatform, migration acceptance criteria should remain separate from UX and CRO hypotheses so launch issues are diagnosable.",
  },
  {
    q: "How long does a Shopify migration take?",
    a: "A bounded migration may take several weeks; complex Shopify Plus projects can take several months. Integration count, source-data quality, custom functionality, international setup, B2B requirements, approvals, content production, and QA usually drive the schedule more than raw SKU count alone.",
  },
  {
    q: "Do customer passwords migrate to Shopify?",
    a: "In standard platform migrations, customer passwords generally cannot be transferred as reusable plaintext credentials. The account migration and reactivation experience should be designed before launch so existing customers understand how to regain access.",
  },
  {
    q: "What should happen after launch?",
    a: "Monitor redirects, 404s, canonical selection, sitemaps, indexation, high-value organic landing pages, analytics, conversion events, integration errors, checkout issues, page performance, and customer-service reports. The first 30 days should be treated as a stabilization and observation period.",
  },
] as const;

export function rankingStructuredData(slug: string): Array<Record<string, unknown>> {
  if (slug !== MIGRATION_RANKING_SLUG) return [];

  const pageUrl = `${SITE_URL}/blog/${MIGRATION_RANKING_SLUG}`;

  const itemList: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${pageUrl}#agency-ranking`,
    name: "10 Best Shopify Migration Agencies in 2026",
    numberOfItems: MIGRATION_AGENCIES.length,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: MIGRATION_AGENCIES.map((name, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Organization",
        name,
        ...(name === "Shugert" ? { url: SITE_URL, "@id": `${SITE_URL}#org` } : {}),
      },
    })),
  };

  const faqPage: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${pageUrl}#faq`,
    mainEntity: MIGRATION_FAQS.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text: a,
      },
    })),
  };

  return [itemList, faqPage];
}
